import { useEffect, useRef, useState } from 'react'
import { useInView, useReducedMotion } from 'framer-motion'

/** Play a finite explanatory sequence once on entry; pause offscreen or in a hidden tab. */
export default function useStoryPlayback(finalStep = 3, intervalMs = 2100) {
  const ref = useRef<HTMLDivElement>(null)
  const inView = useInView(ref, { amount: .35 })
  const reducedMotion = useReducedMotion()
  const started = useRef(false)
  const [visible, setVisible] = useState(true)
  const [step, setStep] = useState(0)
  const [playing, setPlaying] = useState(false)
  const [run, setRun] = useState(0)

  useEffect(() => {
    const sync = () => setVisible(document.visibilityState === 'visible')
    sync()
    document.addEventListener('visibilitychange', sync)
    return () => document.removeEventListener('visibilitychange', sync)
  }, [])

  useEffect(() => {
    if (reducedMotion) {
      setStep(finalStep)
      setPlaying(false)
    } else if (inView && !started.current) {
      started.current = true
      setStep(0)
      setPlaying(true)
    }
  }, [inView, reducedMotion, finalStep])

  useEffect(() => {
    if (!playing || !inView || !visible || reducedMotion || step >= finalStep) return
    const timer = window.setTimeout(() => {
      setStep(step + 1)
      if (step + 1 === finalStep) setPlaying(false)
    }, intervalMs)
    return () => window.clearTimeout(timer)
  }, [step, playing, inView, visible, reducedMotion, finalStep, run, intervalMs])

  const restart = () => {
    started.current = true
    setRun(value => value + 1)
    setStep(reducedMotion ? finalStep : 0)
    setPlaying(!reducedMotion)
  }
  const seek = (value: number) => {
    setPlaying(false)
    setStep(Math.max(0, Math.min(finalStep, value)))
  }
  const toggle = () => {
    if (reducedMotion) seek(finalStep)
    else if (step === finalStep) restart()
    else setPlaying(value => !value)
  }
  return { ref, step, playing, running: playing && inView && visible, run, reducedMotion, restart, seek, toggle }
}
